import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import { Modal, ModalFuss, darfAbbrechen } from "@kundeninfo/ui";
import { Hinweis, Optionen } from "@kundeninfo/ui/me-design";
import { Suchfeld } from "../../components/meHuellen.js";
import { MILESTONE_TEXT } from "./milestoneTexts.js";
import "./MilestonePanel.css";

/**
 * DIE SUCHE ZUM VERKNÜPFEN (Spec D21, Review Aufgabe 8 G3/G4) — ein Modal für „Ticket verknüpfen …“ im Meilenstein-Panel
 * und „Mit Meilenstein verknüpfen …“ im Reiter „Verknüpfungen“: Suchfeld, Suche entprellt (nur die jüngste Antwort zählt;
 * ohne Text sofort, wenn `immediate`, sonst gar nicht), schon Verknüpftes steht nicht zur Wahl, Treffer als `Optionen`,
 * Wahl + „Verknüpfen“ ist die Bestätigung. `onLink` liefert `null` bei Erfolg (dann schließt die Suche) oder den Fehlertext —
 * er steht HIER, wo gearbeitet wird (am Telefon liegt die Suche vollflächig über dem Panel). „Abbrechen“ mit Wahl fragt.
 */

export const SEARCH_MS = 300;

export type LinkSearchProps<T extends { id: string }> = {
  title: string; searchName: string; placeholder: string;
  search: (q: string) => Promise<readonly T[]>;
  /** Ohne Suchtext sofort suchen (offene Meilensteine) statt zu warten (Tickets). */
  immediate: boolean;
  label: (t: T) => string;
  linked: ReadonlySet<string>;
  emptyText: string;
  onLink: (id: string) => Promise<string | null>;
  onDone: () => void;
};

export function LinkSearch<T extends { id: string }>(p: LinkSearchProps<T>): ReactElement {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<readonly T[] | null>(null);
  const [searchFailed, setSearchFailed] = useState(false);
  const [choice, setChoice] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { search, immediate } = p;
  useEffect(() => {
    const s = q.trim();
    if (s === "" && !immediate) { setHits(null); setSearchFailed(false); return undefined; }
    let active = true;
    const run = () => search(s)
      .then((r) => { if (active) { setHits(r); setSearchFailed(false); } })
      .catch(() => { if (active) { setHits([]); setSearchFailed(true); } });
    const timer = s === "" ? undefined : setTimeout(() => { void run(); }, SEARCH_MS);
    if (s === "") void run();
    return () => { active = false; clearTimeout(timer); };
  }, [search, immediate, q]);
  const offered = (hits ?? []).filter((t) => !p.linked.has(t.id));

  async function link(): Promise<void> {
    if (choice === "" || busy) return;
    setBusy(true);
    setError(null);
    const failure = await p.onLink(choice);
    setBusy(false);
    if (failure === null) p.onDone();
    else setError(failure);
  }
  async function cancel(): Promise<void> {
    if (!busy && await darfAbbrechen(choice !== "")) p.onDone();
  }
  return (
    <Modal titel={p.title} onAbbrechen={() => { void cancel(); }} abbrechenGesperrt={busy}>
      <div className="ms__search">
        <Suchfeld name={p.searchName} platzhalter={p.placeholder} wert={q} gesperrt={busy}
          onText={(w) => { setQ(w); setChoice(""); setError(null); }} />
        {searchFailed && <p className="ms__empty" role="status">{MILESTONE_TEXT.searchError}</p>}
        {hits !== null && !searchFailed && offered.length === 0 && <p className="ms__empty">{p.emptyText}</p>}
        {offered.length > 0 && (
          <Optionen beschriftung={MILESTONE_TEXT.hits} wert={choice} gesperrt={busy}
            onGeaendert={(e) => { setChoice(e.wert ?? ""); setError(null); }}>
            {offered.map((t) => <option key={t.id} value={t.id}>{p.label(t)}</option>)}
          </Optionen>
        )}
        {error !== null && <Hinweis art="fehler"><span className="ms__message">{error}</span></Hinweis>}
      </div>
      <ModalFuss speichernText={MILESTONE_TEXT.link} speichernGesperrt={choice === "" || busy} abbrechenGesperrt={busy}
        onSpeichern={() => { void link(); }} onAbbrechen={() => { void cancel(); }} />
    </Modal>
  );
}
